import React, { useEffect, useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useIsMobile } from '../hooks/useIsMobile'
import { cn } from '../lib/utils'

// Pull-to-refresh with a deliberate, native-feeling pull. The previous
// react-simple-pull-to-refresh implementation fired on a 70px swipe
// (refresh-on-flinch). This custom version:
//   - tracks raw finger movement, translates content with 0.5x damping
//   - requires ~50% viewport finger travel before release will fire
//   - renders a smooth circular arc that fills as the user drags,
//     scaling up and switching to the accent colour at the threshold

const PULL_RESISTANCE = 0.5
const THRESHOLD_RATIO = 0.25
const MAX_PULL_RATIO = 0.35
const SPINNER_SLOT_HEIGHT = 72

// Robust "are we at the top" check. The app's real scrolling container
// isn't the document — it's `<div class="flex-1 overflow-auto">` in
// App.jsx. So reading `window.scrollY` or `document.scrollingElement`
// gives 0 forever, which made the pull handler think every touch was a
// valid pull-from-top and preventDefault'd the upward scroll on every
// box / card. This walks up from the wrapper element looking for the
// nearest scrollable ancestor and reads its scrollTop directly.
function findScrollableAncestor(el) {
  let p = el?.parentElement
  while (p && p !== document.body) {
    const style = window.getComputedStyle(p)
    const oy = style.overflowY
    // Match by overflow setting alone — don't require scrollHeight
    // > clientHeight. On first mount the page may not have content
    // yet, but we still need the ancestor reference so subsequent
    // touchstarts can read the live scrollTop without falling back
    // to the document (which is always 0 in this app).
    if (oy === 'auto' || oy === 'scroll') {
      return p
    }
    p = p.parentElement
  }
  return null
}

function getScrollTop(el) {
  const scroller = findScrollableAncestor(el)
  if (scroller) return scroller.scrollTop
  const root = document.scrollingElement || document.documentElement
  return root?.scrollTop ?? window.scrollY ?? 0
}

function isAtTop(el) {
  return getScrollTop(el) <= 0
}

export default function PullToRefresh({ onRefresh, children }) {
  const isMobile = useIsMobile()
  const containerRef = useRef(null)
  const startYRef = useRef(0)
  const isPullingRef = useRef(false)
  const pullDistanceRef = useRef(0)
  const refreshingRef = useRef(false)
  const thresholdRef = useRef(0)
  const maxPullRef = useRef(0)
  const [pullDistance, setPullDistance] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  const [animating, setAnimating] = useState(false)

  const setPull = (v) => {
    pullDistanceRef.current = v
    setPullDistance(v)
  }

  useEffect(() => {
    if (!isMobile) return undefined
    const recompute = () => {
      const h = window.innerHeight
      thresholdRef.current = h * THRESHOLD_RATIO
      maxPullRef.current = h * MAX_PULL_RATIO
    }
    recompute()
    window.addEventListener('resize', recompute)

    const el = containerRef.current
    if (!el) return () => window.removeEventListener('resize', recompute)

    // Hard-reset all touch state. Called on mount (in case a prior
    // navigation left refs hot — refs survive a re-render but should
    // never survive an unmount, except React strict-mode mounts run
    // setup twice without unmounting in between) and on any guard
    // that needs to bail cleanly.
    const resetTouchState = () => {
      isPullingRef.current = false
      startYRef.current = 0
      if (pullDistanceRef.current !== 0) setPull(0)
    }
    resetTouchState()

    const onStart = (e) => {
      // Always start from a known state so a stale isPullingRef
      // (e.g. from a previous gesture whose touchend never fired
      // because the page unmounted mid-touch) can't poison the new
      // sequence.
      isPullingRef.current = false
      if (!isAtTop(el) || refreshingRef.current) return
      startYRef.current = e.touches[0].clientY
      isPullingRef.current = true
      setAnimating(false)
    }

    const onMove = (e) => {
      if (!isPullingRef.current || refreshingRef.current) return
      // If the page scrolled away from the top mid-gesture (e.g.
      // momentum scroll), cancel the pull and yield to the browser.
      if (!isAtTop(el)) {
        isPullingRef.current = false
        if (pullDistanceRef.current !== 0) setPull(0)
        return
      }
      const delta = e.touches[0].clientY - startYRef.current
      if (delta <= 0) {
        // Upward drag = user wants to scroll DOWN through content.
        // Release the pull state entirely so the browser owns the
        // rest of this touch sequence and the next touchmoves don't
        // get re-interpreted. Skip the setPull(0) churn unless we
        // actually need to reset the visual.
        isPullingRef.current = false
        if (pullDistanceRef.current !== 0) setPull(0)
        return
      }
      if (e.cancelable) e.preventDefault()
      const damped = Math.min(delta * PULL_RESISTANCE, maxPullRef.current)
      setPull(damped)
    }

    const onEnd = async () => {
      if (!isPullingRef.current) {
        // Make sure a stale pullDistance from a cancelled drag
        // doesn't leave the content shifted.
        if (pullDistanceRef.current > 0) {
          setAnimating(true)
          setPull(0)
        }
        return
      }
      isPullingRef.current = false
      setAnimating(true)
      if (pullDistanceRef.current >= thresholdRef.current) {
        refreshingRef.current = true
        setRefreshing(true)
        setPull(0)
        try {
          await onRefresh()
        } finally {
          refreshingRef.current = false
          setRefreshing(false)
        }
      } else {
        setPull(0)
      }
    }

    // When the tab goes background mid-pull or the user navigates
    // away, fire a cleanup so the next mount sees a clean slate.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') resetTouchState()
    }

    el.addEventListener('touchstart', onStart, { passive: true })
    el.addEventListener('touchmove', onMove, { passive: false })
    el.addEventListener('touchend', onEnd)
    el.addEventListener('touchcancel', onEnd)
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      resetTouchState()
      window.removeEventListener('resize', recompute)
      el.removeEventListener('touchstart', onStart)
      el.removeEventListener('touchmove', onMove)
      el.removeEventListener('touchend', onEnd)
      el.removeEventListener('touchcancel', onEnd)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [isMobile, onRefresh])

  if (!isMobile) return <>{children}</>

  const progress = thresholdRef.current > 0
    ? Math.min(pullDistance / thresholdRef.current, 1)
    : 0
  const ready = progress >= 1
  const visibleSlot = refreshing ? SPINNER_SLOT_HEIGHT : pullDistance
  const spring = 'cubic-bezier(0.34, 1.56, 0.64, 1)'
  const ease = animating ? `transform 0.45s ${spring}` : 'none'
  const slotEase = animating ? `height 0.45s ${spring}` : 'none'

  return (
    <div ref={containerRef} className="relative">
      <div
        className="absolute top-0 inset-x-0 flex items-center justify-center pointer-events-none overflow-hidden"
        style={{ height: visibleSlot, transition: slotEase }}
        aria-hidden="true"
      >
        <PullIndicator progress={progress} refreshing={refreshing} ready={ready} />
      </div>
      <div style={{ transform: `translateY(${visibleSlot}px)`, transition: ease }}>
        {children}
      </div>
    </div>
  )
}

function PullIndicator({ progress, refreshing, ready }) {
  const size = 36
  const stroke = 2.5
  const radius = (size - stroke) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference * (1 - progress)
  // Bubble scales from 0.6 up to 1.0 as the user pulls — the indicator
  // looks deliberate rather than appearing at full size from frame one.
  const scale = refreshing ? 1 : 0.6 + Math.min(progress, 1) * 0.4

  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-full backdrop-blur',
        'shadow-sm transition-colors duration-300',
        ready || refreshing
          ? 'bg-app-primary-500/15 ring-1 ring-app-primary-500/40'
          : 'bg-card/70 ring-1 ring-border/60'
      )}
      style={{
        width: size + 14,
        height: size + 14,
        transform: `scale(${scale})`,
        transition: 'transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.3s ease-out, box-shadow 0.3s ease-out',
      }}
    >
      {refreshing ? (
        <Loader2 className="w-5 h-5 animate-spin text-app-primary-500" />
      ) : (
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className={cn(
            'transition-colors duration-300',
            ready ? 'text-app-primary-500' : 'text-muted-foreground/70'
          )}
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={stroke}
            opacity={0.18}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={stroke}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{ transition: 'stroke-dashoffset 0.2s cubic-bezier(0.16, 1, 0.3, 1)' }}
          />
        </svg>
      )}
    </div>
  )
}
